import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Picture from "../components/Picture";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import CTABand from "../components/sections/CTABand";
import { BRANDS, BRAND_GROUPS } from "../data/brands";

export default function BrandsIndex() {
    // Pre-group the brands by category id so we render in the spec order.
    const byGroup = BRAND_GROUPS.map((g) => ({
        ...g,
        items: BRANDS.filter((b) => b.group === g.id),
    }));

    return (
        <div data-testid="brands-index-page">
            <SEO
                title="Trusted Partners — 12 Luxury Home Technology Brands"
                description="Certified integrator for Control4, Savant, Lutron, Sonos, Bang & Olufsen, K-Array, Kaleidescape, Samsung, Josh.ai, CoeLux, Sony, and Ubiquiti."
                canonical="https://actluxe.net/brands"
            />

            {/* Hero band */}
            <section className="bg-[#0B0B0C] pt-20 md:pt-28 pb-14 md:pb-20 border-b border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <SectionLabel number="Trusted" label="Partners" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.04] max-w-4xl">
                            The tools of our{" "}
                            <span className="italic text-[#C9A86A]">craft.</span>
                        </h1>
                        <p className="lg:col-span-4 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-2">
                            We work in the platforms that have earned their
                            place — through decades of engineering, dealer
                            stewardship, and the considered taste of the
                            principals we serve.
                        </p>
                    </div>
                </div>
            </section>

            {/* Grouped grid */}
            <section className="bg-[#0B0B0C] py-16 md:py-24">
                <div className="shell space-y-16 md:space-y-20">
                    {byGroup.map((group) => (
                        <div
                            key={group.id}
                            data-testid={`brands-group-${group.id}`}
                        >
                            <div className="flex items-baseline gap-4 mb-8 md:mb-10">
                                <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                    {group.label}
                                </span>
                                <span className="flex-1 h-px bg-[rgba(201,168,106,0.18)]" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                                {group.items.map((brand, idx) => (
                                    <motion.div
                                        key={brand.slug}
                                        suppressHydrationWarning
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.2 }}
                                        transition={{
                                            duration: 0.7,
                                            delay: idx * 0.05,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        <Link
                                            to={`/brands/${brand.slug}`}
                                            className="group block border border-[rgba(201,168,106,0.18)] bg-[#15161A]/60 hover:border-[rgba(201,168,106,0.55)] hover:shadow-[0_0_40px_-12px_rgba(201,168,106,0.35)] transition-all duration-500 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]"
                                            data-testid={`brand-card-${brand.slug}`}
                                        >
                                            <div className="relative aspect-[16/10] overflow-hidden bg-[#1C1D22]">
                                                <Picture
                                                    slug={brand.hero}
                                                    alt={`${brand.name} — ${brand.specialty}`}
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/85 via-[#0B0B0C]/20 to-transparent" />
                                            </div>
                                            <div className="p-6 md:p-7">
                                                <h3 className="font-serif text-2xl md:text-[26px] text-[#F3EFE7] leading-[1.15] transition-colors duration-500 group-hover:text-[#D9BC80]">
                                                    {brand.name}
                                                </h3>
                                                <p className="mt-2.5 text-[#9A958A] text-[13.5px] leading-[1.7]">
                                                    {brand.specialty}
                                                </p>
                                                <div className="mt-5 flex items-center justify-between gap-3">
                                                    <span className="text-[10.5px] tracking-[0.28em] uppercase text-[#C9A86A] transition-colors duration-300 group-hover:text-[#D9BC80]">
                                                        Explore
                                                    </span>
                                                    <span className="inline-flex items-center justify-center w-8 h-8 border border-[#C9A86A]/40 text-[#C9A86A] transition-all duration-500 ease-out group-hover:bg-[#C9A86A] group-hover:text-[#0B0B0C]">
                                                        <ArrowUpRight
                                                            size={13}
                                                            strokeWidth={1.2}
                                                        />
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <CTABand />
        </div>
    );
}
